(() => {
  'use strict';

  if (window.RFOnlineEnquiryBackup) return;

  const proxyPath = '/apps/online-enquiry/record';
  const queueKey = 'rf-online-enquiry-backup:v1';
  const successContextKey = 'rf-online-enquiry-success-context:v1';
  const queueLifetime = 24 * 60 * 60 * 1000;
  const inFlightRequestIds = new Set();
  const productEnquirySources = new Set(['Ask Price', 'Product Enquiry']);
  // The backup endpoint accepts a fixed set of source values, so product-level
  // enquiries (including normal-price "Product Enquiry" submissions) are
  // recorded against its product-enquiry source.
  const backupSourceAliases = { 'Product Enquiry': 'Ask Price' };

  function debugLog(...args) {
    if (window.RF_BACKUP_DEBUG === true) console.log('[RF Backup]', ...args);
  }

  function readQueue() {
    try {
      const stored = JSON.parse(sessionStorage.getItem(queueKey) || '[]');
      const cutoff = Date.now() - queueLifetime;
      return Array.isArray(stored) ? stored.filter((item) => item.queued_at >= cutoff).slice(-10) : [];
    } catch (_error) {
      return [];
    }
  }

  function writeQueue(queue) {
    try {
      sessionStorage.setItem(queueKey, JSON.stringify(queue.slice(-10)));
    } catch (_error) {
      // The backup must never interfere with Shopify's native contact form.
    }
  }

  function removeFromQueue(requestId) {
    writeQueue(readQueue().filter((item) => item.request_id !== requestId));
  }

  function storeSuccessContext(payload) {
    if (!payload) return;
    try {
      sessionStorage.setItem(successContextKey, JSON.stringify({
        requestId: payload.request_id || '',
        source: payload.enquiry_type || payload.source || '',
        products: String(payload.product || '').split(' | ').map((value) => value.trim()).filter(Boolean),
        colours: String(payload.selected_colour || '').split(' | ').map((value) => value.trim()).filter(Boolean),
        selectedColourText: payload.selected_colour || '',
        sourceUrl: payload.source_url || '',
        submittedAt: new Date().toISOString()
      }));
    } catch (_error) {
    }
  }

  function valueByName(form, name) {
    const field = form.elements.namedItem(name);
    return field && typeof field.value === 'string' ? field.value.trim() : '';
  }

  function valueBySelector(form, selector) {
    const field = form.querySelector(selector);
    return field && typeof field.value === 'string' ? field.value.trim() : '';
  }

  function setValue(form, selector, value) {
    const field = form.querySelector(selector);
    if (field) field.value = value || '';
  }

  function selectedProductOptions(scope) {
    const values = [];
    scope.querySelectorAll('variant-selects').forEach((picker) => {
      picker.querySelectorAll('fieldset').forEach((fieldset) => {
        const checked = fieldset.querySelector('input[type="radio"]:checked');
        if (checked && checked.value) values.push(checked.value.trim());
      });
      picker.querySelectorAll('select').forEach((select) => {
        if (select.value) values.push(select.value.trim());
      });
    });
    return values.filter(Boolean);
  }

  function syncProductFields(form, requestId) {
    if (form.dataset.rfCollectionEnquiry === 'true') {
      const sourceUrl = valueByName(form, 'contact[Source URL]') || pageUrl();
      setValue(form, '[data-rf-enquiry-source-url]', sourceUrl);
      setValue(form, '[data-rf-enquiry-uuid]', requestId);
      return {
        colour: valueByName(form, 'contact[Selected Colour]'),
        variant: valueByName(form, 'contact[Variant]'),
        variantId: valueByName(form, 'contact[Variant ID]'),
        sourceUrl,
      };
    }

    const scope = form.closest('product-info') || document.querySelector('product-info') || document;
    const viewer = scope.querySelector('rf-colour-viewer[data-option-name]');
    const colourName = viewer ? viewer.dataset.optionName || '' : '';
    const colourPosition = viewer ? viewer.dataset.optionPosition || '' : '';
    const colourRadio = colourName
      ? Array.from(scope.querySelectorAll('variant-selects input[type="radio"]:checked')).find(
          (input) => input.name === `${colourName}-${colourPosition}`
        )
      : null;
    const variantIdInput = scope.querySelector('form[action*="/cart/add"] input[name="id"]');
    const optionValues = selectedProductOptions(scope);
    const sourceUrl = pageUrl();

    const colourField = form.querySelector('[data-rf-enquiry-colour]');
    const colourChoices = Array.from(form.querySelectorAll('[data-rf-enquiry-colour-choice]:checked'));
    if (colourField && colourField.hasAttribute('data-rf-enquiry-multi-colour')) {
      const selectedColours = colourChoices.map((input) => input.value.trim()).filter(Boolean);
      colourField.value = selectedColours.join(', ');
    } else if (!colourField || colourField.tagName !== 'SELECT') {
      setValue(form, '[data-rf-enquiry-colour]', colourRadio ? colourRadio.value.trim() : '');
    }
    setValue(form, '[data-rf-enquiry-variant]', optionValues.join(' / '));
    setValue(form, '[data-rf-enquiry-variant-id]', variantIdInput ? variantIdInput.value : '');
    setValue(form, '[data-rf-enquiry-source-url]', sourceUrl);
    setValue(form, '[data-rf-enquiry-uuid]', requestId);

    return {
      colour: valueByName(form, 'contact[Selected Colour]'),
      variant: valueByName(form, 'contact[Variant]'),
      variantId: valueByName(form, 'contact[Variant ID]'),
      sourceUrl,
    };
  }

  document.addEventListener('change', (event) => {
    const choice = event.target.closest('[data-rf-enquiry-colour-choice]');
    if (!choice) return;

    const form = choice.closest('form');
    if (!form) return;

    const hidden = form.querySelector('[data-rf-enquiry-multi-colour]');
    const summary = form.querySelector('[data-rf-enquiry-colour-summary]');
    const selected = Array.from(form.querySelectorAll('[data-rf-enquiry-colour-choice]:checked'))
      .map((input) => input.value.trim())
      .filter(Boolean);

    if (hidden) hidden.value = selected.join(', ');
    if (summary) {
      summary.textContent = selected.length
        ? `${selected.length} colour${selected.length === 1 ? '' : 's'} selected`
        : 'No colour selected — optional';
    }
  });

  function compactLines(lines) {
    return lines.filter(Boolean).join('\n');
  }

  function productTitle() {
    const heading = document.querySelector('.product__title h1, main h1');
    return heading ? heading.textContent.trim() : '';
  }

  function pageTitle() {
    const product = productTitle();
    if (product) return product;

    const heading = document.querySelector('main h1');
    return heading ? heading.textContent.trim() : (document.title || '').trim();
  }

  function pageUrl() {
    const canonical = document.querySelector('link[rel="canonical"]');
    const href = canonical && canonical.href ? canonical.href : '';
    const base = href || `${window.location.origin}${window.location.pathname}`;
    return base.split('?')[0].split('#')[0];
  }

  function withSource(details, url, form) {
    return compactLines([
      details,
      `Landing URL: ${valueByName(form, 'contact[Landing URL]')}`,
      url ? `Source: ${url}` : '',
    ]);
  }

  function identifyForm(form) {
    const formId = form.getAttribute('id') || '';

    if (formId.startsWith('rf_quote_form_')) return 'Free Measure';
    if (formId.startsWith('rfcontact_form_')) {
      return form.classList.contains('ldpage_form') ? 'Landing Page' : 'Contact';
    }

    const explicitSource = form.dataset.rfEnquirySource || form.getAttribute('data-rf-enquiry-source') || '';
    if (form.dataset.rfOnlineEnquiry === 'true' && explicitSource === 'Ask Price') return 'Ask Price';
    if (form.dataset.rfOnlineEnquiry === 'true' && explicitSource) return explicitSource;

    return '';
  }

  function buildDetails(form, source, product) {
    if (source === 'Free Measure') {
      return compactLines([
        `Postcode: ${valueByName(form, 'contact[Postcode]')}`,
        `Suburb: ${valueByName(form, 'contact[Suburb]')}`,
        `Flooring Type: ${valueByName(form, 'contact[Flooring Type]')}`,
        `Property Type: ${valueByName(form, 'contact[Property Type]')}`,
        `Approximate Area: ${valueByName(form, 'contact[Approximate Area]')}`,
        `Project Details: ${valueByName(form, 'contact[body]')}`,
        `Existing Enquiry Source: ${valueByName(form, 'contact[Enquiry Source]')}`,
      ]);
    }

    if (source === 'Contact' || source === 'Landing Page') {
      return compactLines([
        `Enquiry Type: ${valueByName(form, 'contact[Enquiry type]')}`,
        `Postcode: ${valueByName(form, 'contact[Postcode]')}`,
        `Suburb: ${valueByName(form, 'contact[Suburb]')}`,
        `Message: ${valueByName(form, 'contact[body]')}`,
        `Campaign: ${valueByName(form, 'contact[Campaign]')}`,
      ]);
    }

    return compactLines([
      `Product: ${product}`,
      valueByName(form, 'contact[Selected Colour]') ? `Selected Colour: ${valueByName(form, 'contact[Selected Colour]')}` : '',


      `Enquiry Type: ${valueByName(form, 'contact[Enquiry Type]') || source}`,
      `Postcode: ${valueByName(form, 'contact[Postcode]')}`,
        `Suburb: ${valueByName(form, 'contact[Suburb]')}`,
      `Message: ${valueByName(form, 'contact[body]')}`,
      `Submission UUID: ${valueByName(form, 'contact[Submission UUID]')}`,
    ]);
  }

  function createRequestId() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }

    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character) => {
      const random = Math.floor(Math.random() * 16);
      const value = character === 'x' ? random : (random & 0x3) | 0x8;
      return value.toString(16);
    });
  }

  function readGa4Attribution() {
    const cookies = Object.fromEntries(document.cookie.split(';').map((item) => {
      const i = item.indexOf('=');
      return i < 0 ? ['', ''] : [item.slice(0, i).trim(), item.slice(i + 1)];
    }).filter(([name]) => name));
    const clientMatch = /^GA\d+\.\d+\.(\d+\.\d+)$/.exec(cookies._ga || '');
    const gaClientId = clientMatch ? clientMatch[1] : '';
    let gaSessionId = '';
    for (const [name, raw] of Object.entries(cookies)) {
      if (!/^_ga_[A-Z0-9]+$/.test(name)) continue;
      const value = decodeURIComponent(raw);
      const match = /^(?:GS1\.\d+\.(\d+)|GS2\.\d+\.s(\d+)(?:\$|\.|$))/.exec(value);
      if (match) { gaSessionId = match[1] || match[2]; break; }
    }
    let landing = window.location.href;
    try { landing = sessionStorage.getItem('rf_enquiry_landing_url') || landing; } catch (_) {}
    let landingParams;
    try { landingParams = new URL(landing, window.location.origin).searchParams; }
    catch (_) { landingParams = new URLSearchParams(); }
    const currentParams = new URL(window.location.href).searchParams;
    const result = { ga_client_id: gaClientId, ga_session_id: gaSessionId };
    for (const key of ['gclid', 'gbraid', 'wbraid']) {
      result[key] = landingParams.get(key) || currentParams.get(key) || '';
    }
    return result;
  }

  function buildPayload(form) {
    const source = identifyForm(form);
    if (!source) return null;
    let jevVisitorId = '';
    try {
      jevVisitorId = localStorage.getItem('rf_visitor_id') || '';
    } catch (_error) {
      jevVisitorId = '';
    }

    const isProductEnquiry = productEnquirySources.has(source);
    const contactPage = valueByName(form, 'contact[Page]');
    const product = valueByName(form, 'contact[Product]') || contactPage || pageTitle();
    const name = valueBySelector(form, '[autocomplete="name"], [name="contact[name]"]');
    // One UUID per physical enquiry submission: generated once, stored on the form
    // and reused by every retry of that same submission. Never regenerated.
    const requestId = form.dataset.rfOnlineEnquiryRequestId || createRequestId();
    form.dataset.rfOnlineEnquiryRequestId = requestId;
    const productFields = isProductEnquiry ? syncProductFields(form, requestId) : {};
    const message = valueByName(form, 'contact[body]');
    const postcode = valueByName(form, 'contact[Postcode]');

    return {
      request_id: requestId,
      submission_id: requestId,
      source: backupSourceAliases[source] || source,
      name: isProductEnquiry && !name ? 'Not provided' : name,
      phone: valueBySelector(form, '[autocomplete="tel"], input[type="tel"]'),
      email: valueBySelector(form, 'input[type="email"]'),
      enquiry_details: withSource(buildDetails(form, source, product), pageUrl(), form),
      product,
      postcode,
      variant: productFields.variant || '',
      variant_id: productFields.variantId || '',
      selected_colour: productFields.colour || '',
      enquiry_type: source,
      message,
      source_url: productFields.sourceUrl || pageUrl(),
      uuid: requestId,
      jev_visitor_id: jevVisitorId,
      ...readGa4Attribution(),
      queued_at: Date.now(),
      // Marked before the request goes out: an attempt that we cannot prove was
      // rejected must never be re-sent, or it creates a second record.
      attempted: false,
    };
  }

  function markAttempted(requestId) {
    const queue = readQueue();
    const entry = queue.find((item) => item.request_id === requestId);
    if (!entry || entry.attempted === true) return false;
    entry.attempted = true;
    writeQueue(queue);
    return true;
  }

  // Only an attempt we know was not accepted (5xx / 429) or whose outcome is
  // unknown (network failure) may be retried, and always with the same submission id.
  // The endpoint is idempotent on that id, so a retry can never create a second record.
  function markRetryable(requestId) {
    const queue = readQueue();
    const entry = queue.find((item) => item.request_id === requestId);
    if (!entry) return false;
    entry.retryable = true;
    writeQueue(queue);
    return true;
  }

  async function deliver(payload, form) {
    if (inFlightRequestIds.has(payload.request_id)) {
      debugLog('delivery already in flight, skipped', payload.request_id);
      return;
    }
    inFlightRequestIds.add(payload.request_id);

    const firstAttempt = markAttempted(payload.request_id);
    debugLog('request started', payload.request_id, firstAttempt ? '(first attempt)' : '(retry)');

    try {
      const response = await fetch(proxyPath, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        credentials: 'same-origin',
        keepalive: true,
      });

      // VPS exclusively owns generate_lead reporting. Browser only confirms backup saved.
      if (response.status === 200 || response.status === 201) {
        const result = await response.json();
        if (result.ok !== true || typeof result.duplicate !== 'boolean') {
          throw new Error('Backup API did not confirm persisted enquiry');
        }
      }

      if (response.ok || (response.status >= 400 && response.status < 500 && response.status !== 429)) {
        // The endpoint accepted (or definitively rejected) this submission, so it
        // must never be sent again.
        removeFromQueue(payload.request_id);
        debugLog(response.status === 200 ? 'already processed' : 'accepted', payload.request_id, response.status);
      } else {
        markRetryable(payload.request_id);
        debugLog('retryable response', payload.request_id, response.status);
      }
    } catch (_error) {
      // Unknown outcome. The endpoint is idempotent on submission_id, so the queue
      // may retry this same id safely.
      markRetryable(payload.request_id);
      debugLog('request failed, queued for retry', payload.request_id);
    } finally {
      inFlightRequestIds.delete(payload.request_id);
      if (form && form.dataset.rfOnlineEnquiryRequestId === payload.request_id) {
        form.dataset.rfOnlineEnquirySubmitting = 'false';
      }
    }
  }

  function queueAndDeliver(form) {
    try {
      // Durable per-form lock: one physical enquiry = one backup submission.
      // It is intentionally never cleared automatically, so a second submit,
      // a double click or Enter-plus-click cannot post the same enquiry again.
      // The same form only gets a new submission id after a real page load.
      if (form.dataset.rfOnlineEnquirySubmitted === '1') {
        debugLog('submit ignored, this form already submitted', form.dataset.rfOnlineEnquiryRequestId);
        return;
      }

      const payload = buildPayload(form);
      if (!payload) return;

      debugLog('submit received', payload.request_id, payload.source, payload.product);

      if (form.dataset.rfCollectionEnquiry === 'true') storeSuccessContext(payload);
      form.dataset.rfOnlineEnquirySubmitted = '1';
      form.dataset.rfOnlineEnquirySubmitting = 'true';

      const queue = readQueue().filter((item) => item.request_id !== payload.request_id);
      queue.push(payload);
      writeQueue(queue);
      void deliver(payload, form);
    } catch (_error) {
      if (form) form.dataset.rfOnlineEnquirySubmitting = 'false';
      // Native Shopify form submission always continues independently.
    }
  }

  function initialise() {
    debugLog('init');

    document.querySelectorAll('form').forEach((form) => {
      if (!identifyForm(form) || form.dataset.rfOnlineEnquiryBound === 'true') return;
      form.dataset.rfOnlineEnquiryBound = 'true';
      form.addEventListener('submit', () => queueAndDeliver(form));
      debugLog('form bound', form.id || form.getAttribute('action'));
    });

    readQueue().forEach((payload) => {
      if (payload.attempted !== true || payload.retryable === true) {
        debugLog('replaying queued payload', payload.request_id);
        void deliver(payload);
        return;
      }
      // Already sent from a previous page view and not reported as failed: replaying
      // it is exactly what produced the "-1" duplicate metaobjects, so it is dropped
      // instead of re-sent.
      debugLog('queued payload already sent, not replayed', payload.request_id);
      removeFromQueue(payload.request_id);
    });
  }

  window.RFOnlineEnquiryBackup = { initialise };
  initialise();
})();
